import { Flex, type FlexProps, Text, type TextProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import React, { type ReactNode } from "react";
import { ButtonGroup } from "ui/button";
import { Chip, type ChipProps } from "ui/chips";

export type TextTitleProps = {
  /**
   * The color theme to use for text colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The relative size for the text elements. All sizes are styled to be responsive.
   */
  size: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

  /**
   * Default alignment of all the elements.
   *
   * @default "start"
   */
  align?: "start" | "center";

  /**
   * Alignment of all the elements for mobile screens only.
   *
   * @default "start"
   */
  mobileAlign?: "start" | "center";

  /**
   * Optional chip element to display above the title.
   */
  chip?: {
    text: string;
    variant?: ChipProps["variant"];
  };

  /**
   * The main title of the element.
   */
  title: ReactNode;

  /**
   * The html element to use for the title.
   *
   * @default "h2"
   */
  titleAs?: TextProps["as"];

  /**
   * An optional description to add body copy below the title.
   */
  description?: ReactNode;

  /**
   * The html element to use for the description.
   *
   * Please use a `div` when passing in MDX values or any other content that could
   * result in a [hydration error](https://nextjs.org/docs/messages/react-hydration-error).
   *
   * @default "p"
   */
  descriptionAs?: TextProps["as"];

  /**
   * Optional max width for the description.
   */
  descriptionMaxW?: FlexProps["maxW"];

  /**
   * Optional additionalContent to display below the title.
   */
  additionalContent?: ReactNode;

  /**
   * Optional CTA buttons, links, etc.
   * Will be wrapped in a `ButtonGroup` to space multiple elements evenly.
   */
  actions?: ReactNode;
} & Omit<FlexProps, "title" | "align">;

export function TextTitle({
  theme = "light",
  align = "start",
  mobileAlign = "start",
  titleAs = "h2",
  descriptionAs = "p",
  size,
  chip,
  title,
  description,
  descriptionMaxW,
  additionalContent,
  actions,
  ...flexProps
}: TextTitleProps) {
  const alignment = { base: mobileAlign, md: align };
  const mx = { base: mobileAlign === "center" ? "auto" : undefined, md: align === "center" ? "auto" : undefined };

  let flexGap: FlexProps["gap"];
  let maxWidth: FlexProps["maxW"];
  let titleTextStyle: TextProps["textStyle"];
  let descriptionTextStyle: TextProps["textStyle"];

  switch (size) {
    case "2xl": {
      flexGap = "bigger";
      maxWidth = "960px";
      titleTextStyle = { base: "display3", md: "display1" };
      descriptionTextStyle = { base: "body2", md: "body1" };
      break;
    }

    case "xl": {
      flexGap = { base: "big", md: "bigger" };
      maxWidth = "840px";
      titleTextStyle = { base: "headline1", md: "display2" };
      descriptionTextStyle = { base: "body2", md: "body1" };
      break;
    }

    case "lg": {
      flexGap = "big";
      maxWidth = "840px";
      titleTextStyle = { base: "headline1", md: "display3" };
      descriptionTextStyle = "body2";
      break;
    }

    case "md": {
      flexGap = { base: "small", md: "big" };
      maxWidth = "680px";
      titleTextStyle = { base: "headline2", md: "headline1" };
      descriptionTextStyle = "body3";
      break;
    }

    case "sm": {
      flexGap = { base: "small", md: "big" };
      maxWidth = "680px";
      titleTextStyle = { base: "headline3", md: "headline2" };
      descriptionTextStyle = { base: "caption1", md: "body3" };
      break;
    }

    case "xs": {
      flexGap = "small";
      maxWidth = { base: "680px", md: "mediaMaxW.mobile" };
      titleTextStyle = "headline3";
      descriptionTextStyle = "caption1";
      break;
    }

    // No default
  }

  return (
    <Flex
      w="100%"
      flexDir="column"
      align={alignment}
      textAlign={alignment}
      color={`brand.text.${theme}`}
      gap={flexGap}
      maxWidth={maxWidth}
      mx={mx}
      {...flexProps}
    >
      {chip && (
        <Chip theme={theme} variant={chip.variant}>
          {chip.text}
        </Chip>
      )}

      <Text as={titleAs} textStyle={titleTextStyle} textAlign={alignment} whiteSpace="pre-wrap">
        {title}
      </Text>

      {description && (
        <Text
          as={descriptionAs}
          textStyle={descriptionTextStyle}
          color={`brand.textSecondary.${theme}`}
          textAlign={alignment}
          maxW={descriptionMaxW}
          mx={mx}
        >
          {description}
        </Text>
      )}

      {additionalContent}

      {actions && <ButtonGroup mt="auto">{actions}</ButtonGroup>}
    </Flex>
  );
}
